import * as React from 'react';
import { debounce } from 'lodash';
import { MessagePropertiesContext } from '../../../../contexts/messageProperties';
import VideoIcon from '../../../../../../svg/icons/video.svg';
import Navigator from '../utils/navigator';

import { MediaSet } from '../../../../types';

interface NavigationProps {
  mediaSet: MediaSet | null;
  slideIndex: number;
  goToSlide: (index: number) => void;
  enableSwipeable?: boolean;
  vertical?: boolean;
}

function Navigation({
  mediaSet,
  slideIndex,
  goToSlide,
  enableSwipeable,
  vertical
}: NavigationProps): React.ReactElement | null {
  const navigationWrapper = React.useRef<HTMLDivElement | null>(null);
  const viewerNavigation = React.useRef<HTMLUListElement | null>(null);
  const prevButton = React.useRef<HTMLButtonElement | null>(null);
  const nextButton = React.useRef<HTMLButtonElement | null>(null);

  const navigationOnResize = React.useRef<() => void>(() => {});
  const navigationEventMap = React.useRef<{ [event: string]: (e: Event) => void }>({});

  const [loadedImages] = React.useState(false);
  const [hasVideo, setHasVideo] = React.useState(false);
  const [formattedMessage] = React.useContext(MessagePropertiesContext);

  React.useEffect(() => {
    const { current: wrapper } = navigationWrapper;

    if (enableSwipeable && loadedImages) {
      const { current: navigation } = viewerNavigation;
      const { current: prev } = prevButton;
      const { current: next } = nextButton;

      if (wrapper === null || navigation === null || prev === null || next === null) {
        return (): void => {};
      }

      const manipulations = Navigator({
        vertical,
        wrapper,
        navigation,
        prev,
        next
      });

      navigationOnResize.current = debounce(manipulations.resize, 0, { trailing: true });
      window.addEventListener('resize', navigationOnResize.current);

      navigationEventMap.current = {
        mousedown: manipulations.mouseDown,
        mousemove: manipulations.mouseMove,
        mouseup: manipulations.mouseUp,
        mouseleave: manipulations.mouseLeave,
        touchstart: manipulations.mouseDown,
        touchmove: manipulations.mouseMove,
        touchend: manipulations.mouseUp
      };
      Object.entries(navigationEventMap.current).forEach(([event, action]) => {
        if (wrapper) {
          wrapper.addEventListener(event, action);
        }
      });
    }

    return (): void => {
      if (enableSwipeable && loadedImages) {
        window.removeEventListener('resize', navigationOnResize.current);
        Object.entries(navigationEventMap.current).forEach(([event, action]) => {
          if (wrapper) {
            wrapper.removeEventListener(event, action);
          }
        });
      }
    };
  }, [enableSwipeable, vertical, loadedImages]);
  const { items = [] } = mediaSet || {};
  const mediaItemsLength = items.length;
  const navElementWidth = 100 / mediaItemsLength;

  const videoButtonClickHandler = (): void => {
    const videoNavItemEl = document.querySelector<HTMLButtonElement>('.viewer-nav__button[data-type="video"]');
    if (videoNavItemEl) {
      videoNavItemEl.click();
    }
  };

  React.useEffect(() => {
    items.map((mediaItem) => mediaItem.type === 'video' && setHasVideo(true));
  }, [hasVideo]);

  if (mediaItemsLength < 2) {
    return null;
  }

  return (
    <div className="viewer-nav__container">
      <button type="button" ref={prevButton} className="viewer-nav__arrows viewer-nav__arrows--prev">
        <span className="a11y-invisible-element">
          {formattedMessage({
            id: 'product.button.prev',
            fallback: 'Previous'
          })}
        </span>
      </button>
      <div className="viewer-nav__wrapper" ref={navigationWrapper}>
        <ul className="viewer-nav" ref={viewerNavigation}>
          {items.map((mediaItem, index) => (
            <li className={`viewer-nav__item ${index === slideIndex ? 'active' : ''}`} key={`nav_${index + 1}`}>
              <div className="viewer-nav__item-container">
                <button
                  type="button"
                  onClick={(): void => goToSlide(index)}
                  className={`viewer-nav__button ${index === slideIndex ? 'viewer-nav__button--active' : ''}`}
                  data-type={mediaItem.type === 'video' ? 'video' : ''}
                  aria-label={`${index + 1} of ${mediaItemsLength}`}
                >
                  <span className="a11y-invisible-element">{index + 1}</span>
                </button>
              </div>
            </li>
          ))}
        </ul>
        <hr style={{ width: `${navElementWidth}%`, marginLeft: `${navElementWidth * slideIndex}%` }} />
      </div>
      <button type="button" ref={nextButton} className="viewer-nav__arrows viewer-nav__arrows--next">
        <span className="a11y-invisible-element">
          {formattedMessage({
            id: 'product.button.next',
            fallback: 'Next'
          })}
        </span>
      </button>
      {hasVideo && (
        <button type="button" className="viewer-nav__video-button" onClick={videoButtonClickHandler}>
          <VideoIcon className="viewer-nav__video-icon" />
          {formattedMessage({
            id: 'product.button.video',
            fallback: 'Video'
          })}
        </button>
      )}
    </div>
  );
}

export default Navigation;
